<template>
  <!-- Account lock: aria-api refuses this tenant (expired / disabled). Rendered from the stored
       tenant, so it holds from the first paint after a restart and while offline. Non-dismissable
       and without a hidden unlock: only a tenant read from the server lifts it. The box follows
       the sales screens' overlay alert (CodOverlayAlert.vue), which gives way to it. -->
  <div
    v-if="inactive"
    class="fixed inset-0 z-50 flex items-center justify-center bg-(--app-scrim)"
    data-testid="account-inactive-lock"
  >
    <div class="account-lock">
      <div class="header text-panel-title">
        <UIcon class="size-5 shrink-0 text-error" name="i-lucide-circle-alert" />
        <span>{{ t('subscription.heading') }}</span>
      </div>
      <div class="message">
        <div>{{ t(messageKey) }}</div>
        <div class="buttons">
          <UButton
            size="lg-plus"
            color="neutral"
            variant="outline"
            class="flex-1"
            :label="t('general.log_out')"
            :disabled="checking"
            data-testid="account-inactive-logout"
            @click="logOut"
          />
          <UButton
            size="lg-plus"
            color="primary"
            variant="solid"
            class="flex-1"
            :label="t('general.retry')"
            :loading="checking"
            data-testid="account-inactive-retry"
            @click="checkAgain"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { ITenant, TenantService, apiService, connectionService } from 'app-core'
import { useAppStore } from '@/stores/useAppStore'
import { useToast } from '@/composables/useToast'

const { t } = useI18n()
const router = useRouter()
const store = useAppStore()
const { toast } = useToast()

const inactive = computed(() => TenantService.isInactive(store.tenant))
const messageKey = computed(() => TenantService.inactiveMessageKey(store.tenant))
const checking = ref(false)

/**
 * Deliver what is queued (aria-api accepts an inactive tenant's sync), then read the tenant from
 * the server. Reactivated: store it (with its offline-login copy) and restart the app, whose boot
 * loads what the refused session never did — stored first, so a boot whose connection check fails
 * does not come back locked. Still inactive: keep the lock and say so.
 */
async function checkAgain() {
  checking.value = true
  try {
    await connectionService.connectionCheck()
    const response = await apiService.get<ITenant>('/me')

    if (!TenantService.isInactive(response.data)) {
      await TenantService.setTenant(response.data, false)
      location.reload()
      return
    }

    TenantService.markInactive(response.data.status)
    toast(t(TenantService.inactiveMessageKey(response.data)), 'error')
  } catch {
    toast(t('connection.internet_offline_message'), 'error')
  } finally {
    checking.value = false
  }
}

async function logOut() {
  await TenantService.logout()
  router.push({ name: 'tenantLogin' })
}
</script>

<style lang="scss" scoped>
// The small-dialog box of CodOverlayAlert.vue: fixed width, buttons stretch to fill it.
.account-lock {
  width: 400px;
  max-width: calc(100vw - 32px);
  background: var(--ui-bg);
  border-radius: var(--radius-frame);
  box-shadow: var(--app-shadow-modal);
  overflow: hidden;

  .header {
    padding: 16px 16px 0;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .message {
    padding: 12px 16px 16px;

    .buttons {
      margin-top: 16px;
      display: flex;
      gap: 8px;
    }
  }
}
</style>
